/* eslint-disable react/prop-types */
import * as React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import { Grid, TextField, MenuItem, Select, FormControl, InputLabel } from '@mui/material';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import { useFormik } from 'formik';
import { postApi } from 'views/services/api';
import { toast } from 'react-toastify';
import Palette from '../../ui-component/ThemePalette';
import { complaintSchema } from 'schema';
import { useNavigate } from 'react-router';
import { Box, Stack, Chip, alpha, useTheme, IconButton, Divider } from '@mui/material';
import {
  Close as CloseIcon,
  BugReport as BugIcon,
  Support as SupportIcon,
  Build as TechnicalIcon,
  Lightbulb as FeatureIcon,
  Send as SendIcon,
  Assignment as ReportIcon
} from '@mui/icons-material';

const AddComplaint = (props) => {
  const { open, handleClose } = props;
  const theme = useTheme();
  const navigate = useNavigate();

  const initialValues = {
    type: '',
    description: ''
  };

  // Tipos de reportes predefinidos
  const reportTypes = [
    { value: 'bug', label: 'Error del Sistema', icon: BugIcon, color: 'error' },
    { value: 'feature', label: 'Solicitud de Mejora', icon: FeatureIcon, color: 'info' },
    { value: 'support', label: 'Soporte Técnico', icon: SupportIcon, color: 'warning' },
    { value: 'technical', label: 'Problema Técnico', icon: TechnicalIcon, color: 'primary' }
  ];

  const AddData = async (values) => {
    try {
      // setIsLoding(true);
      values.hotelId = JSON.parse(localStorage.getItem('hotelData')).hotelId;
      let response = await postApi('api/complaint/add', values);
      if (response.status === 200) {
        formik.resetForm();
        toast.success('Reporte enviado exitosamente');
        navigate('/dashboard/complaint');
      } else {
        toast.error(response.response.data.error);
      }
    } catch (e) {
      toast.error(response.data.error);
    } finally {
      // setIsLoding(false);
    }
  };

  const formik = useFormik({
    initialValues,
    validationSchema: complaintSchema,
    onSubmit: async (values) => {
      AddData(values);
      handleClose();
    }
  });

  const getTypeIcon = (type) => {
    const typeData = reportTypes.find((t) => t.value === type);
    if (!typeData) return <ReportIcon />;
    const IconComponent = typeData.icon;
    return <IconComponent sx={{ fontSize: 20, color: theme.palette[typeData.color].main }} />;
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: theme.shadows[12]
        }
      }}
    >
      {/* Header */}
      <DialogTitle sx={{ p: 0 }}>
        <Box
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.error.main} 0%, ${theme.palette.error.dark} 100%)`,
            color: 'white',
            p: 3
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="h5" fontWeight={700}>
                Reportar Problema
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                Describe el problema técnico para obtener asistencia
              </Typography>
            </Box>
            <IconButton
              onClick={handleClose}
              sx={{
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.1)'
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </Box>
      </DialogTitle>

      {/* Content */}
      <DialogContent sx={{ p: 3 }}>
        <form>
          <Stack spacing={3}>
            {/* Tipo de Reporte */}
            <Box>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Tipo de Reporte
              </Typography>
              <FormControl fullWidth error={formik.touched.type && Boolean(formik.errors.type)}>
                <Select
                  id="type"
                  name="type"
                  value={formik.values.type}
                  onChange={formik.handleChange}
                  displayEmpty
                  sx={{ borderRadius: 2 }}
                  renderValue={(selected) => {
                    if (!selected) {
                      return <Typography color="text.secondary">Selecciona el tipo de problema</Typography>;
                    }
                    const typeData = reportTypes.find((t) => t.value === selected);
                    return (
                      <Stack direction="row" alignItems="center" spacing={1}>
                        {getTypeIcon(selected)}
                        <Typography>{typeData?.label}</Typography>
                      </Stack>
                    );
                  }}
                >
                  {reportTypes.map((type) => {
                    const IconComponent = type.icon;
                    return (
                      <MenuItem key={type.value} value={type.value}>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: 2,
                              backgroundColor: alpha(theme.palette[type.color].main, 0.1),
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <IconComponent sx={{ fontSize: 18, color: theme.palette[type.color].main }} />
                          </Box>
                          <Box>
                            <Typography variant="body2" fontWeight={500}>
                              {type.label}
                            </Typography>
                          </Box>
                        </Stack>
                      </MenuItem>
                    );
                  })}
                </Select>
                {formik.touched.type && formik.errors.type && (
                  <Typography variant="caption" color="error" sx={{ mt: 1, ml: 2 }}>
                    {formik.errors.type}
                  </Typography>
                )}
              </FormControl>
            </Box>

            {/* Vista previa del tipo seleccionado */}
            {formik.values.type && (
              <Box
                sx={{
                  p: 2,
                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  {getTypeIcon(formik.values.type)}
                  <Typography variant="body2" fontWeight={500}>
                    Tipo seleccionado: {reportTypes.find((t) => t.value === formik.values.type)?.label}
                  </Typography>
                </Stack>
              </Box>
            )}

            <Divider />

            {/* Descripción del Problema */}
            <Box>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Descripción del Problema
              </Typography>
              <TextField
                id="description"
                name="description"
                placeholder="Describe detalladamente el problema que estás experimentando..."
                fullWidth
                multiline
                rows={4}
                value={formik.values.description}
                onChange={formik.handleChange}
                error={formik.touched.description && Boolean(formik.errors.description)}
                helperText={formik.touched.description && formik.errors.description}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2
                  }
                }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Proporciona la mayor cantidad de detalles posible para acelerar la resolución
              </Typography>
            </Box>

            {/* Información adicional */}
            <Box
              sx={{
                p: 2,
                backgroundColor: alpha(theme.palette.info.main, 0.05),
                borderRadius: 2,
                borderLeft: `4px solid ${theme.palette.info.main}`
              }}
            >
              <Typography variant="body2" color="text.secondary">
                <strong>Tip:</strong> Incluye pasos para reproducir el problema, mensajes de error específicos, y cualquier acción que hayas
                realizado antes de que ocurriera el problema.
              </Typography>
            </Box>
          </Stack>
        </form>
      </DialogContent>

      {/* Actions */}
      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            textTransform: 'none',
            borderRadius: 2,
            px: 3
          }}
        >
          Cancelar
        </Button>
        <Button
          onClick={formik.handleSubmit}
          variant="contained"
          startIcon={<SendIcon />}
          disabled={!formik.values.type || !formik.values.description}
          sx={{
            textTransform: 'none',
            borderRadius: 2,
            px: 3,
            backgroundColor: theme.palette.error.main,
            '&:hover': {
              backgroundColor: theme.palette.error.dark
            },
            '&:disabled': {
              backgroundColor: alpha(theme.palette.error.main, 0.3)
            }
          }}
        >
          Enviar Reporte
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddComplaint;
